import React, { useEffect, useRef, useState } from 'react';

interface NeighbourhoodMapProps
	{
	lat: number;
	lng: number;
	title: string;
	apiKey: string;
	mapId: string;
	zoom: number;
	}

declare global
	{
	interface Window
		{
		google: any;
		oceanNeighbourhoodMapLoader?: Promise<void>;
		}
	}

// One shared loader promise for the whole page: the Maps script is a singleton,
// so a second <script> for it would re-execute the API and throw. Several
// instances of this block (or another Maps block alongside it) therefore await
// the same load rather than each starting one.
const loadMaps = (apiKey: string, mapId: string): Promise<void> =>
	{
	if (window.google?.maps?.marker)
		{
		return Promise.resolve();
		}

	if (!window.oceanNeighbourhoodMapLoader)
		{
		window.oceanNeighbourhoodMapLoader = new Promise<void>((resolve, reject) =>
			{
			const callbackName = 'oceanInitNeighbourhoodMap';

			(window as any)[callbackName] = () =>
				{
				delete (window as any)[callbackName];
				resolve();
				};

			const script = document.createElement('script');
			// libraries=marker is required for AdvancedMarkerElement, which a
			// cloud-styled map needs -- the legacy Marker is deprecated and
			// unsupported on maps that carry a mapId.
			// loading=async is a URL parameter in its own right, separate from the
			// element's async attribute below -- Maps warns about the bootstrap
			// being synchronous unless the parameter is present.
			script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`
				+ `&map_ids=${encodeURIComponent(mapId)}`
				+ '&libraries=marker'
				+ '&loading=async'
				+ `&callback=${callbackName}`;
			script.async = true;
			script.defer = true;
			script.onerror = () =>
				{
				// Cleared so a later instance can retry rather than awaiting a
				// promise that can never settle.
				window.oceanNeighbourhoodMapLoader = undefined;
				reject(new Error('Failed to load Google Maps'));
				};
			document.head.appendChild(script);
			});
		}

	return window.oceanNeighbourhoodMapLoader;
	};

export const NeighbourhoodMap = ({ lat, lng, title, apiKey, mapId, zoom }: NeighbourhoodMapProps) =>
	{
	const mapRef = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() =>
		{
		let cancelled = false;

		loadMaps(apiKey, mapId)
			.then(() =>
				{
				if (cancelled || !mapRef.current) return;

				const position = { lat, lng };

				const map = new window.google.maps.Map(mapRef.current, {
					zoom,
					center: position,
					mapId,
					// The block is a presentational locator, not a tool -- the
					// default UI would put Street View, map-type and fullscreen
					// controls over a styled map the design treats as artwork.
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
					});

				new window.google.maps.marker.AdvancedMarkerElement({
					map,
					position,
					title,
					});
				})
			.catch(() =>
				{
				if (!cancelled) setError('Failed to load Google Maps');
				});

		return () => { cancelled = true; };
		}, [lat, lng, title, apiKey, mapId, zoom]);

	if (error)
		{
		return <div className="property-neighbourhood__map-error">{error}</div>;
		}

	return <div ref={mapRef} className="property-neighbourhood__map-canvas" />;
	};
